import { nameInitials } from '@/components/enc/format';
import { EditIconButton, GridTable } from '@/components/enc/grid-table';
import { InitialsAvatar, PageHeader, StatusPill } from '@/components/enc/primitives';
import { Button } from '@/components/ui/button';
import { ContentLoader } from '@/components/ui/loaders';
import { Roles } from '@/lib/selectors/roles';
import { cn } from '@/lib/utils';
import { useUsers } from '@/services/users';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import type { User } from 'generated/prisma/browser';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export const Page = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useUsers({ perPage: 500, orderBy: 'name', order: 'asc' });

  const handleEdit = (id: string) => navigate(`/admin/users/${id}/edit`);

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: 'name',
        header: 'NOME',
        cell: ({ row: { original: u } }) => (
          <div
            onClick={() => handleEdit(u.id)}
            className="flex min-w-0 cursor-pointer items-center gap-3"
          >
            <InitialsAvatar text={nameInitials(u.name)} src={u.avatar} />
            <span className="truncate text-sm font-semibold text-ink">{u.name}</span>
          </div>
        ),
      },
      {
        id: 'email',
        header: 'E-MAIL',
        cell: ({ row: { original: u } }) => (
          <span className="block truncate text-[13px] text-body">{u.email}</span>
        ),
      },
      {
        id: 'role',
        header: 'PERFIL',
        cell: ({ row: { original: u } }) => (
          <span
            className={cn(
              'inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold',
              u.role === 'ADMIN' ? 'border-brand text-brand' : 'border-line-2 text-body',
            )}
          >
            {Roles[u.role]}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'STATUS',
        cell: ({ row: { original: u } }) => <StatusPill active={u.isActive} />,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row: { original: u } }) => (
          <EditIconButton onClick={() => handleEdit(u.id)} />
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col gap-5">
      <ContentLoader show={isLoading} />

      <PageHeader
        eyebrow="CONFIGURAÇÕES"
        title="Usuários"
        description="Pessoas da equipe com acesso ao painel administrativo."
        actions={
          <Button onClick={() => navigate('/admin/users/add')}>Novo usuário</Button>
        }
      />

      <GridTable
        table={table}
        minWidth={720}
        columns="minmax(0,2.2fr) minmax(0,2fr) minmax(0,1fr) minmax(0,.8fr) 40px"
        empty={!isLoading && 'Nenhum usuário cadastrado.'}
      />
    </div>
  );
};

export default Page;
